import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { COPY } from "../shared/copy";
import { PushSetup } from "../components/PushSetup";
import { PronounPicker } from "../components/PronounPicker";
import { Avatar } from "../components/Avatar";
import { personName } from "../shared/format";
import { useCrew } from "../lib/crew";

// The first ninety seconds, in the order that loses the fewest people.
//
// Close friends forgive friction. Forty cousins and their spouses do not, and
// every extra tap loses somebody. So this is three steps, each doing one thing,
// and it ends by dropping them into their first check-in rather than on a home
// screen with nothing to do.
//
// The order is deliberate. Name first, because it is the cheapest possible ask
// and it is what everybody else sees next to their answers. Notifications
// second, with the reason stated BEFORE the phone asks, since a reflexive "don't
// allow" is close to unrecoverable and push is how anybody finds out a drop
// happened. Check-in last, while curiosity is still up.

type Step = "name" | "notifications" | "go";

export function Onboarding() {
  const { leagueId } = useParams();
  const { user } = useAuth();
  const { isHost } = useCrew();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("name");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user) return;
      const { data } = await supabase
        .from("users").select("display_name").eq("id", user.id).maybeSingle();
      if (!alive) return;
      // Joining derives a starting name from the email, so this is usually
      // prefilled with something plausible they can correct in one tap rather
      // than an empty box asking them to start from nothing.
      setName(personName(data?.display_name, ""));
      setLoaded(true);
    })();
    return () => { alive = false; };
  }, [user]);

  async function saveName() {
    if (!user || !name.trim()) return;
    setSaving(true);
    await supabase.from("users").update({ display_name: name.trim() }).eq("id", user.id);
    setSaving(false);
    setStep("notifications");
  }

  const dots = (
    <div className="progress" aria-label={`Step ${["name", "notifications", "go"].indexOf(step) + 1} of 3`}>
      {(["name", "notifications", "go"] as Step[]).map((s, i) => (
        <span
          key={s}
          className={`progress-pip${i <= ["name", "notifications", "go"].indexOf(step) ? " done" : ""}`}
        />
      ))}
    </div>
  );

  if (!loaded) return <div className="app"><div className="loading">One second…</div></div>;

  if (step === "name") {
    return (
      <div className="app">
        {dots}
        <h1>Welcome in.</h1>
        <p className="muted">
          Two quick things and you&apos;re playing.
        </p>

        <div className="card">
          <span className="tag">What should the crew call you?</span>
          <div className="row" style={{ gap: 12, alignItems: "center" }}>
            <Avatar id={user?.id ?? ""} name={name || "You"} size={48} />
            <input
              className="input"
              value={name}
              placeholder="Your name"
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <p className="muted">This shows up next to your answers.</p>
        </div>

        {user && <PronounPicker userId={user.id} current={null} compact />}

        <button className="btn primary" disabled={saving || !name.trim()} onClick={() => void saveName()}>
          {saving ? "Saving…" : "Next"}
        </button>
      </div>
    );
  }

  if (step === "notifications") {
    return (
      <div className="app">
        {dots}
        {/* The why comes before the phone asks. A reflexive "don't allow" is
            close to unrecoverable, and push is the entire way anybody finds out
            a drop happened. */}
        <h1>{COPY.notifOnboarding.headline}</h1>
        <p className="muted">{COPY.notifOnboarding.body}</p>

        <PushSetup leagueId={leagueId!} />

        <button className="btn ghost" onClick={() => setStep("go")}>Continue</button>
        <p className="muted center">You can set this up later from the You tab.</p>
      </div>
    );
  }

  return (
    <div className="app">
      {dots}
      <h1>Last thing.</h1>
      <p className="muted">
        {isHost
          ? "Now set the season up: how long it runs, when drops land, and who's in. It takes a couple of minutes and nothing is locked in."
          : "The questions are about you and your people, so the crew needs something to go on. Tell them what you've been up to and we'll build from there."}
      </p>
      {!isHost && (
        <div className="card live">
          <span className="tag">Nothing goes out without your say so</span>
          <p className="muted">
            You approve every question before anyone sees it, and you can pull any of them later.
          </p>
        </div>
      )}
      {/* Straight into it while curiosity is still up, rather than a home screen
          with nothing to do on it. */}
      <button
        className="btn primary"
        onClick={() => navigate(`/league/${leagueId}/${isHost ? "commissioner" : "checkin"}`)}
      >
        {isHost ? "Set up the season" : "Tell the crew what's good"}
      </button>
      <button className="btn ghost" onClick={() => navigate(`/league/${leagueId}`)}>
        I&apos;ll do it later
      </button>
    </div>
  );
}
